import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../api/axiosConfig';
import receptionBg from '../assets/images/backgrounds/reception-bg.png';
import './VisitorEntries.css';

const VisitorEntries = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [filters, setFilters] = useState({
        formType: '',
        status: '',
        search: ''
    });
    const [suggestions, setSuggestions] = useState([]);

    const formTypeLabels = {
        general: 'General Visitor',
        delivery: 'Delivery',
        interview: 'Interview',
        meeting: 'Meeting',
        contractor: 'Contractor'
    };

    const fetchEntries = async () => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams();
            if (filters.formType) params.append('formType', filters.formType);
            if (filters.status) params.append('status', filters.status);

            const response = await axiosInstance.get(`/api/forms?${params.toString()}`);

            if (response.data.success) {
                let filteredEntries = response.data.forms;
                
                if (filters.search) {
                    const searchLower = filters.search.toLowerCase();
                    filteredEntries = filteredEntries.filter(entry => 
                        entry.formData.visitorName?.toLowerCase().includes(searchLower) ||
                        entry.formData.name?.toLowerCase().includes(searchLower) ||
                        entry.formData.candidateName?.toLowerCase().includes(searchLower) ||
                        entry.formData.courierName?.toLowerCase().includes(searchLower) ||
                        entry.formData.contactNumber?.includes(searchLower) ||
                        entry.formData.phone?.includes(searchLower) ||
                        entry.formData.contact?.includes(searchLower) ||
                        entry.formData.purpose?.toLowerCase().includes(searchLower) ||
                        entry.formData.position?.toLowerCase().includes(searchLower)
                    );
                }

                setEntries(filteredEntries);
            }
        } catch (err) {
            console.error('Error fetching entries:', err);
            setError('Failed to load visitor entries');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEntries();
    }, [filters.formType, filters.status]);

    useEffect(() => {
        if (filters.search.length >= 2) {
            const searchLower = filters.search.toLowerCase();
            const uniqueSuggestions = new Set();
            
            entries.forEach(entry => {
                const visitorName = entry.formData.visitorName || entry.formData.name || '';
                const contactNumber = entry.formData.contactNumber || '';
                const purpose = entry.formData.purpose || '';
                
                if (visitorName.toLowerCase().includes(searchLower)) {
                    uniqueSuggestions.add(visitorName);
                }
                if (contactNumber.includes(searchLower)) {
                    uniqueSuggestions.add(contactNumber);
                }
                if (purpose.toLowerCase().includes(searchLower)) {
                    uniqueSuggestions.add(purpose);
                }
            });
            
            setSuggestions(Array.from(uniqueSuggestions).slice(0, 10));
        } else {
            setSuggestions([]);
        }
    }, [filters.search, entries]);

    const handleSearch = (e) => {
        e.preventDefault();
        fetchEntries();
    };

    const handleVerify = async (entryId) => {
        if (!window.confirm('Verify this visitor and add to Register Book?')) {
            return;
        }

        try {
            const response = await axiosInstance.post(`/api/forms/${entryId}/verify`, {});

            if (response.data.success) {
                setSuccess('Visitor verified and added to Register Book!');
                fetchEntries();
                setTimeout(() => setSuccess(''), 3000);
            }
        } catch (err) {
            console.error('Error verifying entry:', err);
            setError(err.response?.data?.message || 'Failed to verify entry');
            setTimeout(() => setError(''), 3000);
        }
    };

    const handleReject = async (entryId) => {
        if (!window.confirm('Reject this visitor entry?')) {
            return;
        }

        try {
            const response = await axiosInstance.post(`/api/forms/${entryId}/reject`, {});

            if (response.data.success) {
                setSuccess('Visitor entry rejected');
                fetchEntries();
                setTimeout(() => setSuccess(''), 3000);
            }
        } catch (err) {
            console.error('Error rejecting entry:', err);
            setError(err.response?.data?.message || 'Failed to reject entry');
            setTimeout(() => setError(''), 3000);
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="visitor-entries-container" style={{ backgroundImage: `url(${receptionBg})` }}>
            <div className="visitor-entries-content">
                <div className="page-header">
                    <img src="/logo.png" alt="Logo" className="app-logo" />
                    <div className="page-header-text">
                        <h1>Visitor Entries</h1>
                        <p>View and verify visitor form submissions</p>
                    </div>
                    <div className="page-header-actions">
                        <div className="user-info">
                            <strong>{user?.fullName || user?.username}</strong>
                        </div>
                        <button onClick={() => navigate('/purpose-selection')} className="btn-back">
                            ← Back
                        </button>
                    </div>
                </div>

                {success && (
                    <div className="alert alert-success">
                        {success}
                    </div>
                )}

                {error && (
                    <div className="alert alert-error">
                        {error}
                    </div>
                )}

                <div className="filters-section">
                    <form onSubmit={handleSearch} className="search-form">
                        <input
                            type="text"
                            placeholder="Search by name, contact, or purpose..."
                            value={filters.search}
                            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                            className="search-input"
                            list="search-suggestions"
                            autoComplete="off"
                        />
                        <datalist id="search-suggestions">
                            {suggestions.map((suggestion, index) => (
                                <option key={index} value={suggestion} />
                            ))}
                        </datalist>
                        <button type="submit" className="btn btn-primary">Search</button>
                    </form>

                    <div className="filter-controls">
                        <select
                            value={filters.formType}
                            onChange={(e) => setFilters({ ...filters, formType: e.target.value })}
                            className="filter-select"
                        >
                            <option value="">All Types</option>
                            <option value="general">General Visitor</option>
                            <option value="delivery">Delivery</option>
                            <option value="interview">Interview</option>
                            <option value="meeting">Meeting</option>
                            <option value="contractor">Contractor</option>
                        </select>

                        <select
                            value={filters.status}
                            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                            className="filter-select"
                        >
                            <option value="">All Status</option>
                            <option value="checked-in">Pending</option>
                            <option value="verified">Verified</option>
                            <option value="rejected">Rejected</option>
                            <option value="checked-out">Checked Out</option>
                        </select>

                        <button onClick={fetchEntries} className="btn btn-secondary">
                            Refresh
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="loading-state">Loading entries...</div>
                ) : entries.length === 0 ? (
                    <div className="empty-state">
                        <p>No visitor entries found</p>
                    </div>
                ) : (
                    <div className="entries-table-container">
                        <table className="entries-table">
                            <thead>
                                <tr>
                                    <th>Date/Time</th>
                                    <th>Visitor Name</th>
                                    <th>Type</th>
                                    <th>Contact</th>
                                    <th>Purpose</th>
                                    <th>Person to Meet</th>
                                    <th>Notes</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {entries.map((entry) => {
                                    // Extract visitor name based on form type
                                    const visitorName = entry.formData.visitorName || 
                                                       entry.formData.name || 
                                                       entry.formData.candidateName ||
                                                       entry.formData.courierName || 
                                                       'N/A';
                                    
                                    // Extract contact based on form type
                                    const contact = entry.formData.contactNumber || 
                                                   entry.formData.contact || 
                                                   entry.formData.phone ||
                                                   entry.formData.phoneNumber || 
                                                   'N/A';
                                    
                                    // Extract purpose based on form type
                                    const purpose = entry.formData.purpose || 
                                                   entry.formData.position || 
                                                   entry.formData.meetingPurpose ||
                                                   entry.formData.deliveryType ||
                                                   entry.formType || 
                                                   'N/A';
                                    
                                    // Extract person to meet based on form type
                                    const personToMeet = entry.formData.personToMeet || 
                                                        entry.formData.interviewerName ||
                                                        entry.formData.hostName ||
                                                        entry.formData.recipientName ||
                                                        entry.formData.meetingWith ||
                                                        'N/A';
                                    
                                    // Extract notes
                                    const notes = entry.notes || 
                                                 entry.formData.notes || 
                                                 entry.formData.additionalNotes || 
                                                 '-';
                                    
                                    return (
                                        <tr key={entry._id}>
                                            <td>{formatDateTime(entry.submittedAt)}</td>
                                            <td>{visitorName}</td>
                                            <td>
                                                <span className="type-badge">
                                                    {formTypeLabels[entry.formType]}
                                                </span>
                                            </td>
                                            <td>{contact}</td>
                                            <td className="purpose-cell">{purpose}</td>
                                            <td>{personToMeet}</td>
                                            <td className="notes-cell" title={notes}>{notes}</td>
                                            <td>
                                                <span className={`status-badge status-${entry.status}`}>
                                                    {entry.status === 'checked-in' ? 'Pending' : 
                                                     entry.status === 'verified' ? 'Verified' : 
                                                     entry.status === 'rejected' ? 'Rejected' :
                                                     entry.status === 'checked-out' ? 'Checked Out' : entry.status}
                                                </span>
                                            </td>
                                            <td>
                                                {entry.status === 'checked-in' && (
                                                    <>
                                                        <button
                                                            className="btn-verify"
                                                            onClick={() => handleVerify(entry._id)}
                                                        >
                                                            Verify
                                                        </button>
                                                        <button
                                                            className="btn-reject"
                                                            onClick={() => handleReject(entry._id)}
                                                        >
                                                            Reject
                                                        </button>
                                                    </>
                                                )}
                                                {entry.status === 'verified' && (
                                                    <span className="verified-text">✓ Verified</span>
                                                )}
                                                {entry.status === 'rejected' && (
                                                    <span className="rejected-text">✗ Rejected</span>
                                                )}
                                                {entry.formData.resumeFile && (
                                                    <button
                                                        className="btn-view-resume"
                                                        onClick={async () => {
                                                            try {
                                                                const response = await axiosInstance.get(
                                                                    `/api/resumes/view/${entry.formData.resumeFile}`,
                                                                    {
                                                                        responseType: 'blob'
                                                                    }
                                                                );
                                                                const url = window.URL.createObjectURL(response.data);
                                                                window.open(url, '_blank');
                                                            } catch (err) {
                                                                console.error('Error viewing resume:', err);
                                                                alert('Failed to load resume');
                                                            }
                                                        }}
                                                        title="View Resume"
                                                    >
                                                        📄
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default VisitorEntries;
